import React, { useState, useEffect } from 'react';
import { Typography, Button, TextField } from '@mui/material';
import { useAppointmentsDestroyMutation } from '../../../services/GLOWClient';
import MuiDialog from '../../../SystemDesign/MuiDialog';
import DeleteIcon from '@mui/icons-material/Delete';
import { useCancelAppointmentMutation } from '../../../services/appointmentWorkflowApi';


const DeleteAppointment = ({ open, onClose, onSuccess, appointment }) => {
    const [dialogClosed, setDialogClosed] = useState(false);
    const [deleteAppointment, { isLoading: isDeleting }] = useAppointmentsDestroyMutation();
    const [cancelAppointment, { isLoading: isCancelling }] = useCancelAppointmentMutation();
    const [reason, setReason] = useState('');
    const isWorkflowCancellation = ['pending', 'proposal_pending', 'proposal_expired'].includes(appointment?.status);
    
    // Reset dialog state when open/close changes
    useEffect(() => {
        if (open === false) {
            setDialogClosed(true);
        } else {
            setDialogClosed(false);
            setReason('');
        }
    }, [open]);
    
    const handleDelete = async () => {
        if (!appointment || !appointment.appointment_id) {
            console.error('No appointment selected for deletion');
            return;
        }
        
        try {
            // Format the data according to the API requirements
            const deleteData = {
                appointmentId: appointment.appointment_id
            };
            
            
            if (isWorkflowCancellation) {
                await cancelAppointment({ id: appointment.appointment_id, reason: reason.trim() }).unwrap();
            } else {
                await deleteAppointment(deleteData).unwrap();
            }
            
            // Call success callback and close dialog
            onSuccess && onSuccess();
            onClose();
        } catch (error) {
            console.error('Failed to delete appointment:', error);
        }
    };
    
    // Content to be displayed in the dialog
    const confirmationContent = (
        <div style={{ padding: '16px 0' }}>
            <Typography variant="body1" style={{ marginBottom: '16px' }}>
                {isWorkflowCancellation ? 'Annuler' : 'Supprimer'} le rendez-vous avec <strong>{appointment?.client_name}</strong> ?
            </Typography>
            {isWorkflowCancellation ? (
                <TextField
                    label="Motif de l’annulation"
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    multiline
                    rows={3}
                    fullWidth
                    required
                />
            ) : (
                <Typography variant="body2" color="error" style={{ marginTop: '8px' }}>
                    Cette action est irréversible et supprimera toutes les données associées à ce rendez-vous.
                </Typography>
            )}
        </div>
    );

    // Trigger button for the dialog - this is hidden as we're controlling the dialog externally
    const triggerButton = <Button style={{ display: 'none' }}>Open</Button>;

    return (
        <MuiDialog
            open={open}
            icon={<DeleteIcon />}
            triggerButton={triggerButton}
            title={isWorkflowCancellation ? 'Annuler le rendez-vous' : 'Supprimer le rendez-vous'}
            subtitle={isWorkflowCancellation ? `Annuler la demande de ${appointment?.client_name || ''}` : `Supprimer définitivement le rendez-vous avec ${appointment?.client_name || ''}`}
            content={confirmationContent}
            existAction={true}
            actionTitle={isWorkflowCancellation ? 'Annuler le rendez-vous' : 'Supprimer'}
            actionFunction={handleDelete}
            onClose={dialogClosed}
            onExternalClose={onClose}
            isLoading={isDeleting || isCancelling}
            actionType="button"
            maxWidth="sm"
            disableAction={isDeleting || isCancelling || (isWorkflowCancellation && !reason.trim())}
        />
    );
};

export default DeleteAppointment;
